import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap } from 'lucide-react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import CredentialsRecovery from './CredentialsRecovery';

export type AuthFormType = 'login' | 'register' | 'recovery';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialForm?: AuthFormType;
  onSuccessfulAuth?: () => void;
}

export default function AuthModal({ isOpen, onClose, initialForm = 'login', onSuccessfulAuth }: AuthModalProps) {
  const [currentForm, setCurrentForm] = useState<AuthFormType>(initialForm);

  // Alternar entre los formularios de inicio de sesión y registro
  const toggleForm = (form: AuthFormType) => {
    setCurrentForm(form);
  };

  // Manejar la autenticación exitosa
  const handleAuthSuccess = (isAdmin = false) => {
    // Llamar a la función de éxito proporcionada si existe
    if (onSuccessfulAuth) {
      onSuccessfulAuth();
    }

    // Solo cerramos el modal si no es un administrador siendo redirigido
    if (!isAdmin) {
      onClose();
    }

    // Ya no recargamos la página automáticamente
    // Esto permite ver los logs y mejora la experiencia de usuario
    // window.location.reload();

    // En su lugar, mostramos un mensaje en la consola
    console.log('✅ Autenticación exitosa completada - La sesión está activa');
  };

  if (!isOpen) return null;

  // Determinar el título según el formulario actual
  const getFormTitle = () => {
    switch (currentForm) {
      case 'login':
        return 'Iniciar sesión';
      case 'register':
        return 'Crear cuenta';
      case 'recovery':
        return 'Recuperar credenciales';
      default:
        return 'Autenticación';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-zinc-900 rounded-2xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-[#FCAA0D]/20 p-2 rounded-full">
                  <Zap className="w-5 h-5 text-[#FCAA0D]" />
                </div>
                <h3 className="text-xl font-bold text-white">
                  {getFormTitle()}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-zinc-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentForm}
                initial={{ opacity: 0, x: currentForm === 'login' ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: currentForm === 'login' ? 20 : -20 }}
                transition={{ duration: 0.2 }}
              >
                {currentForm === 'login' ? (
                  <LoginForm
                    onSuccess={handleAuthSuccess}
                    onRegisterClick={() => toggleForm('register')}
                    onRecoveryClick={() => toggleForm('recovery')}
                  />
                ) : currentForm === 'register' ? (
                  <RegisterForm
                    onSuccess={handleAuthSuccess}
                    onLoginClick={() => toggleForm('login')}
                  />
                ) : (
                  <CredentialsRecovery
                    onClose={() => toggleForm('login')}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
